import type { FunctionComponent, PropsWithChildren } from 'react';
import { useContext } from 'react';
import styled from 'styled-components';
import type { Google } from './types';
import { GoogleMapLink } from './subcomponent/GoogleMapLink/GoogleMapLink';
import { PcaMapContext } from '../../../contexts/PcaMapContext/PcaMapContext';
import { radiusHelper } from '@property.com.au/ui-kit';

export type MarkerFactory = (args: { google: Google; map: google.maps.Map }) => google.maps.Marker;

export interface GeometryMultiPolygon {
  type: 'MultiPolygon';
  coordinates: (number | null)[][][][] | null;
}
export interface GeometryPolygon {
  type: 'Polygon';
  coordinates: (number | null)[][][] | null;
}

export interface GeoJsonData {
  type: 'FeatureCollection';
  features: {
    type: 'Feature';
    properties?: Record<string, string | number>;
    geometry: {
      type: 'MultiPolygon' | 'Polygon';
      coordinates?: (number | null)[][][][] | (number | null)[][][] | null;
    };
  }[];
}
export const isGeometryMultiPolygon = (
  geometry: GeoJsonData['features'][0]['geometry'],
): geometry is GeometryMultiPolygon => geometry.type === 'MultiPolygon';
export const isGeometryPolygon = (
  geometry: GeoJsonData['features'][0]['geometry'],
): geometry is GeometryPolygon => geometry.type === 'Polygon';

export type OnMapLoaded = (args: { google: Google; map: google.maps.Map }) => void;

const PcaMapContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${radiusHelper('medium')};
`;

export const PcaMap: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { ref, initialConfig } = useContext(PcaMapContext);
  const center = initialConfig?.center;
  return (
    <>
      <PcaMapContainer data-testid="pca-map" ref={ref} role="application" />
      <noscript>
        <GoogleMapLink center={center} outboundLinkType={initialConfig?.outboundLinkType} />
      </noscript>
      {children}
    </>
  );
};
